import { init, mainButton, miniApp } from '@telegram-apps/sdk';
import {
  Banner,
  Caption,
  Cell,
  Input,
  List,
  Modal,
  Multiselectable,
  Placeholder,
  Section,
  Select,
  Snackbar,
} from '@telegram-apps/telegram-ui';
import { Icon28Edit } from '@telegram-apps/telegram-ui/dist/icons/28/edit';
import {
  initBackButton,
  initHapticFeedback,
  useInitData,
  useLaunchParams,
  usePopup,
  useThemeParams,
} from '@tma.js/sdk-react';
import { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../../LanguageProvider';
import { createOrder } from '../../api/createOrderRCA';
import { getOffers } from '../../api/getOffersRCA';
import { sendPaymentToChat } from '../../api/sendPaymentToChat';
import { DateInput } from '../../components/shared/DateInput';
import { Offer } from '../../interfaces/offer';
import {
  capitalizeFirstLetter,
  formatDateRange,
  isValidDate,
} from '../../utils/shared';
import {
  formatDateForAPI,
  getNextTenDays,
  getNextTenDaysForAPI,
  isNumeric,
  validateIDNO,
  validateIDNP,
} from './constants';

const initialFormData = {
  idnx: '',
  certificateNumber: '',
  duration: '365',
  startDate: getNextTenDaysForAPI()[0],
  startDateData: '',
  company: '',
};

export const RcaForm = () => {
  const { translate, language } = useLanguage();
  const HapticFeedback = initHapticFeedback();
  const initData = useInitData();

  init();

  const themeParams = useThemeParams();
  const [buttonText, setButtonText] = useState(translate('calculate'));
  const [isButtonDisabled, setButtonDisabled] = useState(true);
  const [isConfirmButtonDisabled, setConfirmButtonDisabled] = useState(true);
  const [isConfirmButtonLoading, setConfirmButtonLoading] = useState(false);
  const [isDateInputValid, setIsDateInputValid] = useState(true);
  const [isFinalDateValid, setIsFinalDateValid] = useState(false);
  const [isButtonLoading, setButtonLoading] = useState(false);
  const [offers, setOffers] = useState<{ offers: Offer[] }>({ offers: [] });
  const [idnxStatus, setIdnxStatus] = useState<
    'default' | 'error' | 'focused' | undefined
  >('default');
  const [certificateNumberStatus, setCertificateNumberStatus] = useState<
    'default' | 'error' | 'focused' | undefined
  >('default');
  const [idnxErrorMessage, setIdnxErrorMessage] = useState('');
  const [carSummary, setCarSummary] = useState('');
  const [isOffersModalOpen, setIsOffersModalOpen] = useState(false);

  const [formData, setFormData] = useState(initialFormData);
  const [isChanged, setIsChanged] = useState(false);

  useEffect(() => {
    mainButton.mount();
    mainButton.setParams({
      isVisible: true,
      isEnabled: true,
      backgroundColor: themeParams.buttonColor,
      textColor: themeParams.buttonTextColor,
    });
    mainButton.onClick(handleMainButtonClick);
    return () => {
      mainButton.setParams({
        isVisible: false,
        isEnabled: false,
      });
    };
  }, []);

  const [isFormValidForSubmit, setIsFormValidForSubmit] = useState(false);
  const [isFirstStepValid, setIsFirstStepValid] = useState(false);
  const formDataRef = useRef(formData);
  useEffect(() => {
    setIsFirstStepValid(
      formData.duration.length !== 0 &&
        formData.certificateNumber.length !== 0 &&
        formData.idnx.length !== 0 &&
        certificateNumberStatus !== 'error' &&
        idnxStatus !== 'error' &&
        isFinalDateValid
    );
    if (
      formData.duration.length !== 0 &&
      formData.certificateNumber.length !== 0 &&
      formData.idnx.length !== 0 &&
      certificateNumberStatus !== 'error' &&
      idnxStatus !== 'error' &&
      isFinalDateValid
    ) {
      setShowSnackBar(false);
    }
  }, [formData, certificateNumberStatus, isFinalDateValid, idnxStatus]);

  useEffect(() => {
    setIsFormValidForSubmit(
      formData.duration.length !== 0 &&
        formData.certificateNumber.length !== 0 &&
        formData.idnx.length !== 0 &&
        certificateNumberStatus !== 'error' &&
        idnxStatus !== 'error' &&
        isFinalDateValid &&
        formData.company.length !== 0
    );
    if (
      !(
        formData.duration.length !== 0 &&
        formData.certificateNumber.length !== 0 &&
        formData.idnx.length !== 0 &&
        certificateNumberStatus !== 'error' &&
        idnxStatus !== 'error' &&
        isFinalDateValid
      )
    ) {
      mainButton.setParams({
        isEnabled: true,
        text: translate('calculate'),
      });
    }
  }, [formData, certificateNumberStatus, isFinalDateValid, idnxStatus]);
  useEffect(() => {
    if (isFormValidForSubmit) {
      mainButton.setParams({
        hasShineEffect: true,
      });
    } else {
      mainButton.setParams({
        hasShineEffect: false,
      });
    }
  }, [isFormValidForSubmit]);
  const isOffersModalOpenRef = useRef(isOffersModalOpen);
  const isFormValidForSubmitRef = useRef(isFormValidForSubmit);
  const isFirstStepValidRef = useRef(isFirstStepValid);
  const certificateNumberStatusRef = useRef(certificateNumberStatus);
  const idnxStatusRef = useRef(idnxStatus);
  useEffect(() => {
    isOffersModalOpenRef.current = isOffersModalOpen;
    isFormValidForSubmitRef.current = isFormValidForSubmit;
    isFirstStepValidRef.current = isFirstStepValid;
    certificateNumberStatusRef.current = certificateNumberStatus;
    idnxStatusRef.current = idnxStatus;
  }, [
    isOffersModalOpen,
    isFormValidForSubmit,
    isFirstStepValid,
    certificateNumberStatus,
    idnxStatus,
  ]);
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  const carSummaryRef = useRef(carSummary);
  useEffect(() => {
    carSummaryRef.current = carSummary;
  }, [carSummary]);
  const [showSnackBar, setShowSnackBar] = useState(false);
  const [snackBarText, setSnackBarText] = useState('');
  const handleMainButtonClick = async () => {
    if (isOffersModalOpenRef.current) {
      setShowSnackBar(false);
      if (isFormValidForSubmitRef.current) {
        try {
          const { certificateNumber, duration, startDate, idnx, company } =
            formDataRef.current;

          setConfirmButtonLoading(true);
          setConfirmButtonDisabled(true);

          const date =
            startDate === 'otherday'
              ? formatDateForAPI(formData.startDateData)
              : startDate;

          mainButton.setParams({
            isLoaderVisible: true,
            hasShineEffect: false,
            isEnabled: false,
          });
          const res = await createOrder(
            date,
            certificateNumber,
            parseInt(duration),
            idnx,
            company,
            initData!.user!.id,
            0,
            process.env.REACT_APP_BOT_NAME
          );

          HapticFeedback.notificationOccurred('success');
          const dateRange = formatDateRange(
            formData.startDate,
            formData.duration,
            formData.startDateData,
            isDateInputValid,
            language
          );

          sendPaymentToChat(
            initData!.user!.id,
            res,
            `${process.env.REACT_APP_URL}/payment?language=${language}&orderId=${res.id}&insuranceType=rca`,
            'rca',
            carSummaryRef.current,
            dateRange,
            language,
            ''
          )
            .then(() => {
              miniApp.close();
            })
            .catch((err) => {
              console.log(err);
              miniApp.close();
            });
        } catch (err) {
          console.error('Order creation failed:', err);
          setConfirmButtonLoading(false);
          setConfirmButtonDisabled(false);
          miniApp.close();
        }
      }
    } else {
      if (isFirstStepValidRef.current) {
        setShowSnackBar(false);
        setIsOffersModalOpen(true);
      } else {
        if (
          formDataRef.current.idnx.length < 13 ||
          idnxStatusRef.current === 'error'
        ) {
          setSnackBarText(translate('snackbar:invalid-idnx'));
        }
        if (
          formDataRef.current.certificateNumber.length < 9 ||
          certificateNumberStatusRef.current === 'error'
        ) {
          setSnackBarText(translate('snackbar:invalid-certificate'));
        }
        setShowSnackBar(true);
        HapticFeedback.notificationOccurred('error');
      }
    }
  };

  useEffect(() => {
    if (isOffersModalOpen) {
      mainButton.setParams({
        text: translate('green-card-form:submit-button'),
        isEnabled: false,
      });
      if (
        formData.duration.length !== 0 &&
        formData.certificateNumber.length !== 0 &&
        formData.idnx.length !== 0 &&
        certificateNumberStatus !== 'error' &&
        idnxStatus !== 'error' &&
        isFinalDateValid &&
        formData.company.length !== 0
      ) {
        mainButton.setParams({
          isEnabled: true,
        });
      }
    } else {
      mainButton.setParams({ text: buttonText });
      if (
        formData.duration.length !== 0 &&
        formData.certificateNumber.length !== 0 &&
        formData.idnx.length !== 0 &&
        certificateNumberStatus !== 'error' &&
        idnxStatus !== 'error' &&
        isFinalDateValid
      ) {
        mainButton.setParams({ isEnabled: true });
      }
    }
  }, [isOffersModalOpen]);

  useEffect(() => {
    if (formData.company !== '') {
      mainButton.setParams({
        isEnabled: true,
      });
    }
  }, [formData.company]);

  useEffect(() => {
    setIsChanged(JSON.stringify(formData) !== JSON.stringify(initialFormData));
  }, [formData]);

  const handleChange = (e: any) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const BackButton = initBackButton();

  const popup = usePopup();
  useEffect(() => {
    BackButton[0].show();

    const handleBackButton = async () => {
      if (isChanged) {
        const buttonId = await popup.open({
          title: translate('alert-unsaved-changes-title'),
          message: translate('alert-unsaved-changes'),
          buttons: [
            { id: 'ok', type: 'ok' },
            { id: 'cancel', type: 'cancel' },
          ],
        });

        if (buttonId === 'ok') {
          window.location.href = '/';
          BackButton[1]();
        }
      } else {
        window.location.href = '/';
        BackButton[1]();
      }
    };
    BackButton[0].on('click', handleBackButton);
    return () => {
      BackButton[0].off('click', handleBackButton);
      BackButton[1]();
    };
  }, [isChanged, translate]);

  const validateIdnx = (e: any) => {
    const isIdno = validateIDNO(e.target.value);
    const isIdnp = validateIDNP(e.target.value);
    if (isIdno || isIdnp) {
      setIdnxStatus('default');
      setIdnxErrorMessage('');
    } else {
      setIdnxStatus('error');
    }
    if (e.target.value.length === 13) {
      if (!isIdno && !isIdnp) {
        HapticFeedback.notificationOccurred('error');
      }
    }
  };

  const validateCertificateNumber = (e: any) => {
    const certificateNumber = e.target.value;
    if (
      certificateNumber.toString().length === 9 &&
      isNumeric(certificateNumber)
    ) {
      setCertificateNumberStatus('default');
    } else {
      setCertificateNumberStatus('error');
    }
  };

  useEffect(() => {
    const isValid = isValidDate(formData.startDateData);
    setIsDateInputValid(isValid);
    if (formData.startDate === 'otherday' && isDateInputValid) {
      setIsFinalDateValid(true);
    } else if (getNextTenDaysForAPI().includes(formData.startDate)) {
      setIsFinalDateValid(true);
    } else setIsFinalDateValid(false);
  }, [formData.startDate, formData.startDateData, isDateInputValid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      formData.duration.length !== 0 &&
      formData.certificateNumber.length !== 0 &&
      formData.idnx.length !== 0 &&
      certificateNumberStatus !== 'error' &&
      idnxStatus !== 'error' &&
      isFinalDateValid
    ) {
      setButtonDisabled(true);
      setButtonLoading(true);
      mainButton.setParams({
        isEnabled: false,
        isLoaderVisible: true,
        hasShineEffect: false,
      });
      getOffers(
        formData.certificateNumber,
        parseInt(formData.duration),
        formData.idnx
      ).then((res) => {
        if (res.hasOwnProperty('error')) {
          setButtonLoading(false);
          mainButton.setParams({
            isLoaderVisible: false,
          });
          if (res.error === 'Vehicle not found') {
            setCertificateNumberStatus('error');
            HapticFeedback.notificationOccurred('error');
          }
          return;
        }
        if (res.offers.length === 0) {
          mainButton.setParams({
            isLoaderVisible: false,
          });
          setSnackBarText(translate('snackbar:no-offers-found'));
          setShowSnackBar(true);
          HapticFeedback.notificationOccurred('error');
          return;
        }
        const lowestPriceOffer = res.offers.reduce(
          (minOffer: any, currentOffer: any) => {
            return currentOffer.price < minOffer.price
              ? currentOffer
              : minOffer;
          }
        );
        setButtonText(
          `${res.offers.length} ${translate('offers-from')} ${
            lowestPriceOffer.price
          } ${lowestPriceOffer.currency})`
        );

        HapticFeedback.notificationOccurred('success');
        setOffers(res);
        setButtonLoading(false);
        setButtonDisabled(false);
        mainButton.setParams({
          isEnabled: true,
          isLoaderVisible: false,
          text: `${res.offers.length} ${translate('offers-from')} ${
            lowestPriceOffer.price
          } ${lowestPriceOffer.currency})`,
        });
      });
    } else {
      setButtonText(translate('calculate'));
      setButtonDisabled(true);
      setButtonLoading(false);
      setConfirmButtonDisabled(true);
      mainButton.setParams({
        isLoaderVisible: false,
        text: translate('calculate'),
      });
    }
  }, [
    formData.certificateNumber,
    formData.duration,
    formData.idnx,
    formData.startDate,
    formData.startDateData,
    isDateInputValid,
    certificateNumberStatus,
    idnxStatus,
    isFinalDateValid,
  ]);

  useEffect(() => {
    setConfirmButtonDisabled(true);
    const carInfo = offers?.offers[0]?.name.split(', ').pop() || '';
    setCarSummary(carInfo);
  }, [offers]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (formData.company !== '') {
      setConfirmButtonDisabled(false);
    } else {
      setConfirmButtonDisabled(true);
    }
  }, [formData.company]);

  const lp = useLaunchParams();
  const so = ['macos', 'ios'].includes(lp.platform) ? 'ios' : 'base';
  return (
    <form style={{ marginTop: '5rem' }}>
      <List
        style={{
          background: 'var(--tgui--secondary_bg_color)',
          height: '100%',
        }}
      >
        <Section header={translate('green-card-form:start-date')}>
          <Select
            name="startDate"
            value={formData.startDate}
            onChange={(e) => {
              handleChange(e);
              HapticFeedback.impactOccurred('light');
            }}
          >
            <option value={getNextTenDaysForAPI()[0]}>
              {translate('today')} ({getNextTenDays()[0]})
            </option>
            <option value={getNextTenDaysForAPI()[1]}>
              {translate('tomorrow')} ({getNextTenDays()[1]})
            </option>
            <option value={getNextTenDaysForAPI()[2]}>
              {translate('day-after-tomorrow')} ({getNextTenDays()[2]})
            </option>
            <option value={getNextTenDaysForAPI()[3]}>
              {`${getNextTenDays()[3]}`}
            </option>
            <option value={getNextTenDaysForAPI()[4]}>
              {`${getNextTenDays()[4]}`}
            </option>
            <option value={getNextTenDaysForAPI()[5]}>
              {`${getNextTenDays()[5]}`}
            </option>
            <option value={getNextTenDaysForAPI()[6]}>
              {`${getNextTenDays()[6]}`}
            </option>
            <option value={getNextTenDaysForAPI()[7]}>
              {`${getNextTenDays()[7]}`}
            </option>
            <option value={getNextTenDaysForAPI()[8]}>
              {`${getNextTenDays()[8]}`}
            </option>{' '}
            <option value={getNextTenDaysForAPI()[9]}>
              {`${getNextTenDays()[9]}`}
            </option>
            {/* <option value="otherday">Altǎ zi ...</option> */}
          </Select>
        </Section>
        {formData.startDate === 'otherday' && (
          <DateInput
            name="startDateData"
            value={formData.startDateData}
            isValid={isDateInputValid}
            onChange={(e: any) => {
              handleChange(e);
            }}
          />
        )}
        <Section header={translate('green-card-form:certificate-number')}>
          <Input
            placeholder="200200200"
            type="text"
            inputMode="numeric"
            maxLength={9}
            name="certificateNumber"
            value={formData.certificateNumber}
            status={certificateNumberStatus}
            onChange={(e) => {
              handleChange(e);
              validateCertificateNumber(e);
            }}
          />
        </Section>
        <Section header={translate('green-card-form:idnp-idno')}>
          <Input
            placeholder="2000400070720"
            name="idnx"
            type="text"
            inputMode="numeric"
            maxLength={13}
            status={idnxStatus}
            value={formData.idnx}
            onChange={(e) => {
              handleChange(e);
              validateIdnx(e);
            }}
          />
        </Section>
        <div
          style={{
            height: '50px',
            backgroundColor: 'var(--tgui--secondary_bg_color)',
          }}
        ></div>
        <Modal
          header={<Modal.Header />}
          onOpenChange={(isOpen) => {
            setIsOffersModalOpen(isOpen);
          }}
          open={isOffersModalOpen}
        >
          <Placeholder
            style={{
              padding: '0px',
              alignItems: 'start',
              marginLeft: `${so === 'ios' ? '18px' : ''}`,
              marginRight: `${so === 'ios' ? '18px' : ''}`,
            }}
          >
            <List
              style={{
                margin: '0',
                marginLeft: '4px',
              }}
            >
              <Banner
                //@ts-ignore
                callout={translate('green-card-form:vehicle')}
                header={carSummary}
                style={{
                  margin: '0',
                  paddingBottom: '0',
                  boxShadow: 'none ',
                  boxSizing: 'content-box',
                }}
              ></Banner>
              <Banner
                //@ts-ignore
                callout={translate('green-card-form:insurance-period-summary')}
                header={formatDateRange(
                  formData.startDate,
                  formData.duration,
                  formData.startDateData,
                  isDateInputValid,
                  language
                )}
                style={{
                  margin: '0',
                  paddingBottom: '0',
                  boxSizing: 'content-box',
                  boxShadow: 'none ',
                }}
              ></Banner>
            </List>
            <Section
              header={translate('green-card-form:insurance-company')}
              style={{
                width: '100%',
                marginBottom: '10px',
              }}
            >
              {offers.offers.map((offer: Offer) => {
                return (
                  <Cell
                    Component="label"
                    style={{
                      paddingTop: '5px',
                      paddingBottom: '5px',
                    }}
                    before={
                      <>
                        <Multiselectable
                          name="company"
                          value={offer.company}
                          checked={formData.company === offer.company}
                          onChange={(e) => {
                            handleChange(e);
                            HapticFeedback.impactOccurred('light');
                          }}
                        />
                        <img
                          loading="eager"
                          style={{
                            marginLeft: '0.75rem',
                          }}
                          width={40}
                          src={`./images/${capitalizeFirstLetter(
                            offer.company
                          )}.png`}
                          alt={capitalizeFirstLetter(offer.company)}
                        />
                      </>
                    }
                    multiline
                    after={
                      <small
                        style={{
                          textAlign: 'center',
                          fontFamily: 'var(--tgui--font-family)',
                        }}
                      >
                        <b>{offer.price} MDL</b>
                      </small>
                    }
                  >
                    {capitalizeFirstLetter(offer.company)}
                  </Cell>
                );
              })}
            </Section>
            <Caption
              Component="p"
              className="text-center"
              level="2"
              weight="3"
              style={{
                textAlign: 'center',
                color: 'var( --tgui--secondary_hint_color)',
                lineHeight: '16px',
                padding: '4px',
              }}
            >
              {translate('offers-disclaimer')}
            </Caption>
          </Placeholder>
        </Modal>
      </List>
      {showSnackBar && (
        <Snackbar
          before={<Icon28Edit />}
          itemRef="e"
          onClose={() => {
            setSnackBarText('');
            setShowSnackBar(false);
          }}
          duration={1600}
          children={translate('snackbar:invalid-form-title')}
          description={snackBarText}
        />
      )}
    </form>
  );
};
